import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import {
  useCancelManualDispatchCardsQueries,
  useToggleCardManuallySentCache,
} from "./useToggleCardManuallySentCache.ts";

export const useToggleCardManuallySent = ({
  cardId,
  projectId,
}: {
  cardId: string;
  projectId: string;
}) => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const toggleCardManuallySentCache = useToggleCardManuallySentCache();
  const cancelManualDispatchCardsQueries =
    useCancelManualDispatchCardsQueries();

  return useMutation({
    mutationKey: ["cards", cardId, "manual-dispatch-sent"],
    // Write after cancel so no in-flight fetch races the optimistic write.
    onMutate: async (isCurrentlySent: boolean) => {
      await cancelManualDispatchCardsQueries();
      toggleCardManuallySentCache(cardId, !isCurrentlySent);
    },
    mutationFn: async (isCurrentlySent: boolean) => {
      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${cardId}/manual-dispatch-sent`,
        {
          method: isCurrentlySent ? "DELETE" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Changing the sent state failed");
      }
    },
    onSuccess: () => {
      displaySuccess(translator("changesSaved"));
    },
    onError: (_, isCurrentlySent: boolean) => {
      toggleCardManuallySentCache(cardId, isCurrentlySent);
      displayError(translator("errorSavingChanges"));
    },
  });
};
